import { OnrampApiError } from '../client.js';
import { forgetCryptoDepositSource, peekCryptoDepositSource, prefetchCryptoDepositSource, rememberCryptoDepositSource, } from './crypto-deposit-source.js';
export function createCryptoDepositController(client, surface, locale, createId = () => crypto.randomUUID(), initialOperationId = null) {
    const prefetched = initialOperationId ? null : peekCryptoDepositSource(client, surface);
    let snapshot = {
        operation: prefetched,
        selectedRoute: prefetched?.instructions?.route ?? prefetched?.routes[0] ?? null,
        busy: false,
        checking: false,
        checkedWithoutTransfer: false,
        showSource: Boolean(prefetched?.instructions) && canEditSource(prefetched),
        error: null,
    };
    let idempotencyKey = null;
    let replacementIdempotencyKey = null;
    let requestedOperationId = initialOperationId;
    let disposed = false;
    let inFlight = false;
    let retryNotBefore = 0;
    let refreshBackoffMs = 7_000;
    const listeners = new Set();
    const completionListeners = new Set();
    let completedOperationId = null;
    const update = (patch) => {
        if (disposed)
            return;
        snapshot = { ...snapshot, ...patch };
        if (snapshot.showSource && !canEditSource(snapshot.operation))
            snapshot.showSource = false;
        for (const listener of listeners)
            listener();
        if (snapshot.operation?.state === 'complete' &&
            snapshot.operation.operationId !== completedOperationId) {
            completedOperationId = snapshot.operation.operationId;
            for (const listener of completionListeners)
                listener(completedOperationId);
        }
    };
    const provider = () => {
        if (!client.cryptoDeposit)
            throw new Error('crypto_deposit_unavailable');
        return client.cryptoDeposit;
    };
    const run = async (action, polling = false) => {
        if (inFlight || disposed || (polling && Date.now() < retryNotBefore))
            return;
        inFlight = true;
        update({ busy: true, error: null });
        try {
            await action();
            if (polling) {
                retryNotBefore = 0;
                refreshBackoffMs = 7_000;
            }
        }
        catch (error) {
            if (polling) {
                const retryAfter = error instanceof OnrampApiError ? error.retryAfterMs : 0;
                retryNotBefore = Date.now() + Math.max(refreshBackoffMs, retryAfter);
                refreshBackoffMs = Math.min(refreshBackoffMs * 2, 60_000);
            }
            update({ error: 'unavailable' });
        }
        finally {
            inFlight = false;
            update({ busy: false });
        }
    };
    const start = () => {
        requestedOperationId = null;
        return run(async () => {
            const operation = await prefetchCryptoDepositSource(client, surface, locale, () => {
                idempotencyKey ??= createId();
                return idempotencyKey;
            });
            update({
                operation,
                selectedRoute: operation.routes[0] ?? null,
                checkedWithoutTransfer: false,
                showSource: true,
            });
        });
    };
    const resume = (id) => {
        requestedOperationId = id;
        return run(async () => {
            const operation = await provider().get(id);
            update({
                operation,
                selectedRoute: operation.instructions?.route ?? operation.routes[0] ?? null,
                checkedWithoutTransfer: false,
                showSource: false,
            });
        });
    };
    return {
        getSnapshot: () => snapshot,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        start,
        resume,
        retry: () => (requestedOperationId ? resume(requestedOperationId) : start()),
        select(routeId) {
            if (!canEditSource(snapshot.operation))
                return;
            if (snapshot.operation?.instructions && !snapshot.showSource)
                return;
            const selectedRoute = snapshot.operation?.routes.find((route) => route.id === routeId);
            if (selectedRoute)
                update({ selectedRoute, error: null, checkedWithoutTransfer: false });
        },
        editSource() {
            if (!snapshot.operation?.instructions || !canEditSource(snapshot.operation))
                return;
            update({
                showSource: true,
                selectedRoute: snapshot.operation.instructions.route,
                checkedWithoutTransfer: false,
            });
        },
        continue: () => run(async () => {
            let { operation, selectedRoute } = snapshot;
            if (!operation || !selectedRoute) {
                update({ error: 'route_unavailable' });
                return;
            }
            if (!canEditSource(operation)) {
                update({ showSource: false });
                return;
            }
            if (operation.instructions) {
                operation = await provider().get(operation.operationId);
                rememberCryptoDepositSource(client, surface, operation);
                update({ operation });
                if (!canEditSource(operation))
                    return;
                if (operation.instructions?.route.id === selectedRoute.id) {
                    update({ showSource: false, checkedWithoutTransfer: false });
                    return;
                }
                const selectedCurrencyId = selectedRoute.currencyId;
                const selectedBlockchainId = selectedRoute.blockchainId;
                forgetCryptoDepositSource(client, surface);
                operation = await prefetchCryptoDepositSource(client, surface, locale, () => {
                    replacementIdempotencyKey ??= createId();
                    return replacementIdempotencyKey;
                });
                requestedOperationId = null;
                selectedRoute =
                    operation.routes.find((candidate) => candidate.currencyId === selectedCurrencyId &&
                        candidate.blockchainId === selectedBlockchainId) ?? null;
                update({ operation, selectedRoute });
                if (!selectedRoute) {
                    update({ error: 'route_unavailable' });
                    return;
                }
            }
            const updated = await provider().instructions(operation.operationId, selectedRoute.id);
            rememberCryptoDepositSource(client, surface, updated);
            update({
                operation: updated,
                checkedWithoutTransfer: false,
                showSource: false,
            });
            replacementIdempotencyKey = null;
        }),
        refresh: () => run(async () => {
            if (snapshot.operation) {
                const operation = await provider().get(snapshot.operation.operationId);
                if (!requestedOperationId)
                    rememberCryptoDepositSource(client, surface, operation);
                update({
                    operation,
                    checkedWithoutTransfer: snapshot.checkedWithoutTransfer &&
                        operation.state === 'deposit_address' &&
                        operation.transfers.length === 0,
                });
            }
        }, true),
        reconcile: () => {
            update({ checking: true });
            return run(async () => {
                if (snapshot.operation) {
                    const operation = await provider().reconcile(snapshot.operation.operationId);
                    if (!requestedOperationId)
                        rememberCryptoDepositSource(client, surface, operation);
                    update({
                        operation,
                        checkedWithoutTransfer: operation.state === 'deposit_address' && operation.transfers.length === 0,
                    });
                }
            }).finally(() => update({ checking: false }));
        },
        onComplete(listener) {
            completionListeners.add(listener);
            return () => {
                completionListeners.delete(listener);
            };
        },
        dispose() {
            disposed = true;
            listeners.clear();
            completionListeners.clear();
        },
    };
}
function canEditSource(operation) {
    return Boolean(operation &&
        operation.transfers.length === 0 &&
        ['source', 'deposit_address', 'checking_transfer'].includes(operation.state));
}
